import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { MdChevronRight, MdExpandMore } from 'react-icons/md'
import { isArray } from 'lodash-es'
import styled from 'styled-components/macro'
import { palette } from 'styled-tools'
import { Flex, Label } from 'components/primitives'
import CallTypeIcon from 'forms/CallForm/CallTypeSelection/CallTypeIcon'
import { CallTypes } from 'types/callstore'
import Divider from 'components/Divider'
import { callEvents } from 'pages/Ticket/routes/Default/Details/CallHistory/CallEvent'
import { monza } from 'theme/colors'
import { formatDispatchTimestamp, UnionCallTypes } from './DispatchAAAEvent'
import { DynamoEvents } from 'types/events'
import { useDispatch, useSelector } from 'react-redux'
import { removeRecentAllstateDispatch } from 'redux/recentDispatch/recentDispatchSlice'
import { useGetAllstateDispatches } from 'hooks/universalPayload'
import { selectRecentDispatches } from 'redux/appStore'
import { useMemo } from 'react'
import { getAllstateDispatchData } from 'components/Dispatch/DispatchAllstateService'

const GridWrapper = styled.div`
  &:not(:last-child) {
    margin-bottom: 8px;
  }

  .grid {
    display: grid;
    grid-template-columns: 50px auto 20px;
    grid-gap: 8px;
  }
`

export const RetryLink = styled(Label as any)`
  cursor: pointer;
  color: ${palette('calypso')};
`

export const DispatchAllstateEventProxy: React.FC<{ event: DynamoEvents }> = ({ event }) => {
  const { eventType, attributes } = event
  const dispatches = (isArray(attributes.dispatchId) ? attributes.dispatchId : [attributes.dispatchId]) as any
  return (
    <>
      {dispatches.map((dispatch, i) => (
        <DispatchAllstateEventProxyDetails
          key={i}
          dispatchId={dispatch}
          event={event}
          eventType={eventType}
          timestamp={formatDispatchTimestamp(event.audit?.[0]?.createTimestamp)}
        />
      ))}
    </>
  )
}

type DetailProps = {
  dispatchId: string // or universal payload type?
  eventType?: string | null
  timestamp?: string
  event?: DynamoEvents
}

const DispatchAllstateEventProxyDetails: React.FC<DetailProps> = ({ dispatchId, event, eventType, timestamp }) => {
  const { t } = useTranslation()
  const reduxDispatch = useDispatch()
  const removeRecentDispatch = (callId: string) => reduxDispatch(removeRecentAllstateDispatch(callId))
  const [toggleOpen, setToggleOpen] = React.useState<boolean>(false)
  const callEventType = UnionCallTypes[eventType?.toUpperCase() as keyof typeof CallTypes]
  // NOTE: In the Allstate POST and Hero events-table, dispatchId is used. In their GET, callId is used. They're the same thing just different names.
  const { data, loading, error, refetch } = useGetAllstateDispatches(dispatchId)
  const Component = callEvents[eventType as keyof typeof callEvents]
  const isCancelled = data?.serviceRequest?.status === 'CANCELLED'
  const eta: string =
    data?.serviceRequest?.eta?.etaMinMinutes != null && data?.serviceRequest?.eta?.etaMaxMinutes != null
      ? data?.serviceRequest?.eta?.etaMinMinutes === data?.serviceRequest?.eta?.etaMaxMinutes
        ? `${data?.serviceRequest?.eta?.etaMinMinutes} ${t('labels.mins')}`
        : `${data?.serviceRequest?.eta?.etaMinMinutes} ${t('labels.mins')} - ${data?.serviceRequest?.eta?.etaMaxMinutes} ${t('labels.mins')}`
      : 'N/A'

  const recentDispatches = useSelector(selectRecentDispatches)
  const dispatchData = useMemo(
    () => getAllstateDispatchData(data, dispatchId, recentDispatches),
    [data, dispatchId, recentDispatches],
  )

  return (
    <>
      <div
        style={{ cursor: 'pointer' }}
        onClick={() => {
          setToggleOpen(!toggleOpen)
          if (!toggleOpen) {
            removeRecentDispatch(data?.serviceRequest.callId)
          }
        }}
      >
        <GridWrapper>
          <div className="grid">
            <div>{eventType && <CallTypeIcon callType={callEventType} gutter="sm" />}</div>
            <Flex style={{ flexDirection: 'column' }}>
              <Flex>
                {!loading && (
                  <Label style={{ fontWeight: 'bold', cursor: 'pointer', marginRight: 20 }}>
                    {`# ${dispatchData?.serviceRequest.callId} - ${callEventType}`}
                  </Label>
                )}
              </Flex>
              {loading && <Label style={{ fontStyle: 'italic' }}>{t('Loading')}</Label>}
              <Flex>{!loading && !error && data && !isCancelled && <Label>{`ETA: ${eta}`}</Label>}</Flex>
              <Flex>
                <Label>{!loading && !error && dispatchData && dispatchData.serviceRequest?.status}</Label>
              </Flex>
              <Flex>{!loading && !error && <Label style={{ whiteSpace: 'nowrap' }}> {timestamp} </Label>}</Flex>
              {!loading && error && (
                <>
                  <Label style={{ color: monza }}>{t('Unable to retrieve status from Allstate')}</Label>
                  <RetryLink
                    onClick={async (event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      await refetch(true)
                    }}
                  >
                    {t('Retry')}
                  </RetryLink>
                </>
              )}
            </Flex>
            <div>{toggleOpen ? <MdExpandMore /> : <MdChevronRight />}</div>
          </div>
        </GridWrapper>
      </div>
      {toggleOpen && (
        <>
          <Divider />
          {Component && <Component {...(event as DynamoEvents)} />}
        </>
      )}
    </>
  )
}

export default DispatchAllstateEventProxy
